import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcwIcon } from 'lucide-react';
import { useState, type JSX } from 'react';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import type { DriveFileVersion } from '@/lib/api/drive-files.js';
import { cn } from '@/lib/utils.js';

import { formatBytes } from './artifact-display.js';
import {
  externalFileKeys,
  listExternalVersions,
  restoreExternalVersion,
  type ExternalFileLocation,
} from './external-file-location.js';

type RestoreStatus = { tone: 'ok' | 'error'; text: string } | null;

/** Where the file lives, as a person names it. */
function sourceName(location: ExternalFileLocation): string {
  return location.source === 'sharepoint' ? 'SharePoint' : 'OneDrive';
}

/** The line under a version: who saved it, when, and how big it is, as far as the source says. */
function versionDetails(version: DriveFileVersion): string {
  return [
    version.lastModifiedBy,
    version.lastModifiedAt
      ? new Date(version.lastModifiedAt).toLocaleString()
      : null,
    version.size === null ? null : formatBytes(version.size),
  ]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ');
}

/**
 * The version history of a SharePoint or OneDrive file, read from the source: view any version in
 * place of the current one, or restore it. Restoring names the eTag the viewer last read, so a file
 * that changed in the meantime is refused, and that refusal is shown as the service words it.
 */
export function ExternalVersionHistory({
  workspaceId,
  location,
  etag,
  canRestore,
  selectedVersionId,
  onSelectVersion,
}: {
  workspaceId: string;
  location: ExternalFileLocation;
  /** The file's current eTag; Restore waits for it. */
  etag: string | null;
  /** False when the source is connected for reading only; Restore is then not offered at all. */
  canRestore: boolean;
  /** The version on screen, or null for the current one. */
  selectedVersionId: string | null;
  onSelectVersion: (versionId: string | null) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: externalFileKeys.versions(workspaceId, location),
    queryFn: () => listExternalVersions(workspaceId, location),
    staleTime: 30_000,
  });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<RestoreStatus>(null);

  const restore = async (versionId: string): Promise<void> => {
    if (etag === null) return;
    setBusy(true);
    setStatus(null);
    try {
      await restoreExternalVersion(workspaceId, location, versionId, etag);
      onSelectVersion(null);
      setStatus({ tone: 'ok', text: 'That version is the current one now.' });
    } catch (error) {
      setStatus({
        tone: 'error',
        text: error instanceof Error ? error.message : 'Could not restore it.',
      });
    } finally {
      setBusy(false);
      void queryClient.invalidateQueries({
        queryKey: externalFileKeys.item(workspaceId, location),
      });
      void queryClient.invalidateQueries({
        queryKey: externalFileKeys.versions(workspaceId, location),
      });
    }
  };

  return (
    <section aria-label="Version history" className="flex flex-col gap-2">
      <p className="text-xs text-muted-foreground">
        The versions the file keeps where it lives. Restoring one makes it the
        current version.
      </p>
      {status ? (
        <p
          role={status.tone === 'error' ? 'alert' : 'status'}
          className={cn(
            'text-xs',
            status.tone === 'error' ? 'text-destructive' : 'text-primary',
          )}
        >
          {status.text}
        </p>
      ) : null}
      {query.isLoading ? (
        <Skeleton className="h-10 w-full rounded-lg" />
      ) : query.isError ? (
        <p className="text-sm text-muted-foreground">
          Couldn&apos;t load the version history. Try again shortly.
        </p>
      ) : (
        <ul className="max-h-56 divide-y divide-border/60 overflow-y-auto rounded-lg border border-border">
          {(query.data?.versions ?? []).map((version) => (
            <VersionRow
              key={version.versionId}
              version={version}
              isViewing={
                selectedVersionId === null
                  ? version.isCurrent
                  : selectedVersionId === version.versionId
              }
              offersRestore={canRestore && !version.isCurrent}
              restoreReady={etag !== null && !busy}
              onView={() =>
                onSelectVersion(version.isCurrent ? null : version.versionId)
              }
              onRestore={() => void restore(version.versionId)}
            />
          ))}
        </ul>
      )}
      {query.data?.truncated ? (
        <p className="text-xs text-muted-foreground">
          {`Older versions are not shown here. Open the file in ${sourceName(location)} for the full history.`}
        </p>
      ) : null}
    </section>
  );
}

function VersionRow({
  version,
  isViewing,
  offersRestore,
  restoreReady,
  onView,
  onRestore,
}: {
  version: DriveFileVersion;
  isViewing: boolean;
  /** Restore is shown at all: an older version, and a source the caller may write to. */
  offersRestore: boolean;
  /** The eTag is known and no restore is running. */
  restoreReady: boolean;
  onView: () => void;
  onRestore: () => void;
}): JSX.Element {
  return (
    <li
      className={cn(
        'flex items-center gap-1 pr-2 transition-colors',
        isViewing ? 'bg-primary/10' : 'hover:bg-muted',
      )}
    >
      <button
        type="button"
        onClick={onView}
        aria-label={`View version ${version.versionId}`}
        aria-pressed={isViewing}
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 px-3 py-2 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-foreground">
              Version {version.versionId}
            </span>
            {version.isCurrent ? (
              <Badge variant="secondary" className="text-[10px]">
                Current
              </Badge>
            ) : null}
          </span>
          <span className="block truncate text-xs text-muted-foreground">
            {versionDetails(version)}
          </span>
        </span>
      </button>
      {offersRestore ? (
        <Button
          variant="ghost"
          size="sm"
          disabled={!restoreReady}
          onClick={onRestore}
          aria-label={`Restore version ${version.versionId}`}
        >
          <RotateCcwIcon className="size-3.5" /> Restore
        </Button>
      ) : null}
    </li>
  );
}
